import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatPercent } from "@/utils/formatters";

type SeriesDatum = {
  id: number | string;
  name: string;
  value: number;
};

type AnalyticsPanelProps = {
  eyebrow: string;
  title: string;
  subtitle?: string;
  /** Time window this panel actually covers. Required whenever it ignores the cycle filter. */
  windowLabel?: string;
  actions?: ReactNode;
  span?: "full" | "wide" | "narrow";
  children: ReactNode;
};

const AnalyticsPanel = ({
  eyebrow,
  title,
  subtitle,
  windowLabel,
  actions,
  span = "wide",
  children,
}: AnalyticsPanelProps) => {
  // No row-span here: panels are grouped into flow/position sections, so a panel that
  // claims two rows leaves a hole whenever a feature flag removes its neighbour.
  const spanClass =
    span === "full"
      ? "lg:col-span-2 xl:col-span-12"
      : span === "narrow"
      ? "lg:col-span-1 xl:col-span-4"
      : "lg:col-span-2 xl:col-span-8";

  return (
    <section className={`app-panel-raised app-reveal min-w-0 overflow-hidden p-4 sm:p-6 ${spanClass}`}>
      <div className={`mb-5 flex flex-col gap-4 ${actions ? "lg:flex-row lg:items-start lg:justify-between" : ""}`}>
        <div className={actions ? "min-w-0 max-w-3xl" : "min-w-0"}>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--app-muted-text)]">
              {eyebrow}
            </p>
            {windowLabel ? (
              <span className="rounded-full bg-[var(--app-neutral-chip-bg)] px-2 py-0.5 text-[11px] font-semibold text-[var(--app-text-secondary)]">
                {windowLabel}
              </span>
            ) : null}
          </div>
          <h2 className="mt-2 text-xl font-semibold leading-tight text-[var(--app-text)] sm:text-2xl">
            {title}
          </h2>
          {subtitle ? <p className="text-sm text-[var(--app-text-secondary)]">{subtitle}</p> : null}
        </div>
        {actions}
      </div>

      {children}
    </section>
  );
};

type SavingsDistributionCardProps = {
  isLoading: boolean;
  data: SeriesDatum[];
  total: number;
  colors: Map<number | string, string>;
  pieColors: string[];
  summary: ReactNode;
};

export const SavingsDistributionCard = ({
  isLoading,
  data,
  total,
  colors,
  pieColors,
  summary,
}: SavingsDistributionCardProps) => {
  const { t } = useTranslation(["analytics", "common"]);

  return (
    <AnalyticsPanel
      span="narrow"
      eyebrow={t("analytics.savingsDistributionEyebrow")}
      title={t("analytics.savingsDistributionTitle")}
      subtitle={t("analytics.savingsDistributionSubtitle")}
      windowLabel={t("analytics.windowToDate")}
    >

      {isLoading ? (
        <div className="py-10 text-center text-[var(--app-text-secondary)]">{t("analytics.loadingShort")}</div>
      ) : data.length === 0 ? (
        <div className="py-10 text-center text-[var(--app-text-secondary)]">{t("analytics.noActivity")}</div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="app-panel-raised-track p-4 sm:p-5">{summary}</div>

          <div className="app-panel-raised-track p-4 sm:p-5">
            <div className="mb-4 flex flex-wrap gap-2 text-xs font-semibold sm:text-sm">
              <span className="rounded-full bg-[var(--app-primary-chip-bg)] px-3 py-1 text-[var(--app-text)]">
                {t("analytics.savingsInstitutionsCount", { count: data.length })}
              </span>
              <span className="rounded-full bg-[var(--app-neutral-chip-bg)] px-3 py-1 text-[var(--app-text-secondary)]">
                {formatCurrency(total)}
              </span>
            </div>
            <div className="space-y-2 xl:max-h-136 xl:overflow-auto xl:pr-1">
              {[...data]
                .sort((first, second) => second.value - first.value)
                .map((item, index) => {
                const percent = total > 0 ? (item.value / total) * 100 : 0;
                return (
                  <div key={`savings-${item.id}`} className="rounded-[var(--ds-radius-lg)] px-3 py-3 hover:bg-[var(--app-row-hover-bg)]">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-sm font-semibold text-[var(--app-text)]">{item.name}</span>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold tabular-nums text-[var(--app-text)]">{formatCurrency(item.value)}</div>
                        <div className="text-[11px] text-[var(--app-text-secondary)]">{formatPercent(percent)}</div>
                      </div>
                    </div>
                    <div className="app-meter mt-2">
                      <div
                        className="app-meter__fill"
                        style={{
                          width: `${Math.max(Math.min(percent, 100), percent > 0 ? 1.5 : 0)}%`,
                          background: colors.get(item.id) ?? pieColors[index % pieColors.length],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </AnalyticsPanel>
  );
};
